'use client';
import { Group, MantineSize, Pagination } from '@mantine/core';
import { usePagination as mUsePagination } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';

interface Params<T> {
  pageSize: number;
  items: T[];
  size?: MantineSize;
  simple?: boolean;
}

const SIZE_TO_GAP: Record<MantineSize, number> = {
  xs: 0.25,
  sm: 0.5,
  md: 1,
  lg: 1.25,
  xl: 1.5,
};

export const usePagination = <T,>({
  pageSize,
  items,
  size = 'md',
  simple = false,
}: Params<T>): [T[], () => JSX.Element | null] => {
  const pagination = mUsePagination({
    total: Math.ceil(items.length / pageSize),
    initialPage: 1,
  });

  const lastPage = Math.ceil(items.length / pageSize);

  const allItems = useRef<number>(items.length);

  const [chunk, setChunk] = useState<T[]>(
    items.slice(
      (pagination.active - 1) * pageSize,
      pageSize * pagination.active
    )
  );

  const renderPagination = () => {
    if (items.length <= pageSize) {
      return null;
    }

    const gap = simple ? SIZE_TO_GAP[size] : SIZE_TO_GAP[size] * 10;

    return (
      <Pagination.Root
        classNames={{
          control: simple ? 'mantine-pagination-button-simple' : '',
        }}
        size={size}
        color="#4fb3cf"
        value={pagination.active}
        total={Math.ceil(items.length / pageSize)}
        onFirstPage={pagination.first}
        onLastPage={pagination.last}
        onNextPage={pagination.next}
        onPreviousPage={pagination.previous}
        onChange={pagination.setPage}
      >
        <Group gap={gap}>
          {pagination.active !== 1 && <Pagination.Previous />}
          <Pagination.Items />
          {pagination.active !== lastPage && <Pagination.Next />}
        </Group>
      </Pagination.Root>
    );
  };

  // on the initial render the items may be an empty array
  // so we need this use effect to capture the total number of initial items
  // this is needed so that we can reset the pagination if the items are filtered
  useEffect(() => {
    if (allItems.current === 0) {
      allItems.current = items.length;
    }
  }, [items]);

  // when the items have been filtered we need to reset the pagination
  useEffect(() => {
    if (allItems.current > 0 && items.length !== allItems.current) {
      pagination.setPage(1);
    }
  }, [items]);

  useEffect(() => {
    const active = pagination.active;
    const slice = items.slice((active - 1) * pageSize, active * pageSize);
    setChunk(slice);
  }, [pagination.active, items]);

  return [chunk, renderPagination];
};
